import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AudioPlayer as CkAudioPlayer } from '@NP-Webber/crm-kit';
import {
  Banner, Button, Dialog, Field, ProgressBar, RadioList, Segmented, Select, Spinner, TextArea, TextInput,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../services/api.js';
import { AudioPlayer, Row, apiError } from '../next/ui';

/**
 * שיגור הודעה — who (the rows the screen passes), which message (uploaded file / existing template / text to read),
 * and how: played on answer, or a tzintuk with the message played to whoever calls back.
 * target: { memberIds, label } — memberIds undefined means all active members.
 * A chosen file plays from the browser and reaches the server only when sending.
 */
const AUDIENCES = [
  { value: 'husband', label: 'נייד הבעל' },
  { value: 'wife', label: 'נייד האישה' },
  { value: 'both', label: 'שניהם' },
];
const SOURCES = [
  { value: 'upload', label: 'העלאת קובץ' },
  { value: 'template', label: 'הקלטה קיימת' },
  { value: 'text', label: 'טקסט להקראה' },
];
const DELIVERIES = [
  { value: 'answer', label: 'הודעה במענה — המערכת מחייגת, ומי שעונה שומע מיד' },
  { value: 'callback', label: 'צינתוק והודעה בחזרה — מי שחוזר שומע לפני הפתיח (עד 7 ימים)' },
];
const MAX_AUDIO_BYTES = 15 * 1024 * 1024;
const POLL_MS = 2000;
const noop = () => {};
const num = (v) => Number(v || 0).toLocaleString('he-IL');

function Progress({ run }) {
  const { stats } = run;
  const done = stats.total - stats.queued;
  return (
    <>
      <p className="nx-desc">{`שולח… ${num(done)} מתוך ${num(stats.total)}`}</p>
      <ProgressBar value={stats.total ? (done * 100) / stats.total : 0} />
    </>
  );
}

function Result({ run }) {
  const { stats } = run;
  const none = !stats.sent && stats.failed;
  return (
    <Banner tone={none ? 'danger' : stats.failed || run.stoppedReason ? 'warning' : 'success'} icon={none ? 'alert' : 'check'}>
      {`נשלח ל־${num(stats.sent)} מספרים`}
      {stats.failed ? `, נכשלו ${num(stats.failed)}` : ''}
      {run.stoppedReason ? ` (${run.stoppedReason})` : ''}
    </Banner>
  );
}

function Form({ target, onClose }) {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [audience, setAudience] = useState('husband');
  const [delivery, setDelivery] = useState('answer');
  const [source, setSource] = useState('upload');
  const [file, setFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [savedFileId, setSavedFileId] = useState(null); // after a test send the file is already on the server
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [ttsText, setTtsText] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [testPhone, setTestPhone] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [run, setRun] = useState(null);
  const running = run?.status === 'running';
  const realRun = run && !run.isTest ? run : null;

  useEffect(() => {
    api.get('/broadcasts/templates').then(({ data }) => setTemplates(data.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setPreviewLoading(true);
    api.post('/broadcasts/preview', { memberIds: target.memberIds, audience, delivery })
      .then(({ data }) => { if (!cancelled) { setPreview(data); setError(''); } })
      .catch((e) => { if (!cancelled) { setPreview(null); setError(apiError(e, 'שגיאה בבדיקת הנמענים')); } })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [target.memberIds, audience, delivery]);

  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(() => {
      api.get(`/broadcasts/${run.id}`).then(({ data }) => setRun(data)).catch(() => {});
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [running, run?.id]);

  useEffect(() => {
    if (!file) { setFileUrl(null); return undefined; }
    const url = URL.createObjectURL(file);
    setFileUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const pickFile = (picked) => {
    if (picked.size > MAX_AUDIO_BYTES) {
      setError('קובץ ההקלטה גדול מ-15MB');
      return;
    }
    setError('');
    setFile(picked);
    setSavedFileId(null);
  };

  const messageReady = source === 'text' ? !!ttsText.trim() : source === 'upload' ? !!file : !!templateId;

  const post = (fields) => {
    if (source === 'text') return api.post('/broadcasts', { ...fields, messageType: 'text', ttsText });
    const fileId = source === 'upload' ? savedFileId : templateId;
    if (fileId) return api.post('/broadcasts', { ...fields, messageType: 'template', templateId: fileId });
    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => {
      if (v !== undefined) fd.append(k, k === 'memberIds' ? JSON.stringify(v) : v);
    });
    fd.append('audio', file, file.name);
    return api.post('/broadcasts', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  };

  const send = async (extra) => {
    setSending(true);
    setError('');
    try {
      const { data } = await post({ audience, delivery, targetLabel: target.label, ...extra });
      setRun(data);
      if (source === 'upload') setSavedFileId(data.templateId);
    } catch (e) {
      setError(apiError(e, 'שגיאה בשיגור ההודעה'));
    } finally {
      setSending(false);
    }
  };

  const blocked = !preview || previewLoading || !preview.count || !preview.enough || preview.quietHours;
  const canSend = !blocked && messageReady && !sending && !running;

  return (
    <Dialog
      open
      onClose={() => !sending && onClose()}
      title="שיגור הודעה"
      icon="phone"
      size="md"
      footer={realRun ? (
        <>
          <Button variant="ghost" onClick={onClose}>סגירה</Button>
          <Button icon="history" onClick={() => { onClose(); navigate('/broadcasts'); }}>מעקב מי שמע</Button>
        </>
      ) : (
        <>
          <Button variant="ghost" disabled={sending} onClick={onClose}>ביטול</Button>
          <Button variant="primary" icon="phone" disabled={!canSend} onClick={() => send({ memberIds: target.memberIds })}>
            {sending ? 'שולח…' : `שיגור ל־${num(preview?.count)}`}
          </Button>
        </>
      )}
    >
      <div className="nx-stack">
        {realRun ? (running ? <Progress run={realRun} /> : <Result run={realRun} />) : (
          <>
            <dl className="nx-kv">
              <div className="nx-kv__row"><dt>נמענים</dt><dd><b>{target.label}</b></dd></div>
            </dl>

            <Field label="למי מחייגים">
              <Segmented ariaLabel="למי מחייגים" value={audience} onChange={setAudience} items={AUDIENCES} />
            </Field>

            <Field label="ההודעה">
              <Segmented ariaLabel="מקור ההודעה" value={source} onChange={setSource} items={SOURCES} />
            </Field>
            {source === 'upload' ? (
              <Row>
                <Button icon="upload" disabled={sending} onClick={() => fileRef.current?.click()}>
                  {file ? 'החלפת קובץ' : 'בחירת קובץ הקלטה'}
                </Button>
                {file ? <span>{file.name}</span> : <span className="ck-muted">mp3, wav, ogg, m4a עד 15MB</span>}
                <input
                  type="file"
                  accept="audio/*,.mp3,.wav,.ogg,.m4a,.webm"
                  hidden
                  ref={fileRef}
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) pickFile(f); }}
                />
              </Row>
            ) : null}
            {source === 'template' ? (
              <Field label="הקלטה">
                <Select
                  value={templateId}
                  onChange={setTemplateId}
                  options={[{ value: '', label: 'בחירת הקלטה…' }, ...templates.map((t) => ({ value: String(t.id), label: t.name }))]}
                />
              </Field>
            ) : null}
            {source === 'text' ? (
              <Field label="טקסט להקראה" hint="ימות מקריאה את הטקסט בקול ממוחשב">
                <TextArea rows={3} value={ttsText} onChange={(e) => setTtsText(e.target.value)} />
              </Field>
            ) : null}
            {source === 'upload' && fileUrl ? <CkAudioPlayer key={fileUrl} src={fileUrl} label="השמעת ההקלטה" compact /> : null}
            {source === 'template' && templateId ? <AudioPlayer key={templateId} src={`/broadcasts/templates/${templateId}/audio`} label="השמעת ההקלטה" onFirstPlay={noop} /> : null}

            <Field label="אופן השיגור">
              <RadioList value={delivery} onChange={setDelivery} options={DELIVERIES} ariaLabel="אופן השיגור" />
            </Field>

            {previewLoading ? <div className="ck-empty"><Spinner size={24} /></div> : preview ? (
              <>
                <dl className="nx-kv">
                  <div className="nx-kv__row">
                    <dt>{delivery === 'callback' ? 'ניידים תקינים' : 'מספרים תקינים'}</dt>
                    <dd><b>{num(preview.count)}</b>{preview.noPhone ? ` (ל־${num(preview.noPhone)} חברים אין מספר מתאים)` : ''}</dd>
                  </div>
                  {preview.cost != null ? <div className="nx-kv__row"><dt>עלות</dt><dd><b>{preview.cost}</b> יחידות (0.1 למספר)</dd></div> : null}
                  <div className="nx-kv__row"><dt>יתרת היחידות</dt><dd><b>{Number(preview.units).toFixed(1)}</b></dd></div>
                </dl>
                {preview.cost == null ? <p className="nx-desc">החיוב על שיחה יוצאת נקבע בימות לפי השיחות בפועל.</p> : null}
                {preview.quietHours ? <Banner tone="warning" icon="alert">{`שעות שקטות (${preview.quietHoursLabel}): אפשר לשגר מ-10:00. שליחת בדיקה למספר אחד אפשרית גם עכשיו.`}</Banner> : null}
                {!preview.enough ? <Banner tone="danger" icon="alert">אין מספיק יחידות לשיגור.</Banner> : null}
                {!preview.count ? <Banner tone="info" icon="info">אין נמענים עם מספר מתאים.</Banner> : null}
              </>
            ) : null}

            <Field label="שליחת בדיקה למספר אחד">
              <Row>
                <TextInput ltr value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="05xxxxxxxx" />
                <Button icon="phone" disabled={!testPhone.trim() || !messageReady || sending || running} onClick={() => send({ testPhone })}>שליחת בדיקה</Button>
              </Row>
            </Field>
            {run?.isTest ? (running ? <Progress run={run} /> : <Result run={run} />) : null}
          </>
        )}
        {error ? <Banner tone="danger" icon="alert">{error}</Banner> : null}
      </div>
    </Dialog>
  );
}

/** the form lives only while open, so every opening starts clean */
export default function MessageBroadcastDialog({ target, onClose }) {
  return target ? <Form target={target} onClose={onClose} /> : null;
}
